import {Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges} from "@angular/core";
import {MaterialModule} from "../../../../../material.module";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule, ValidationErrors,
  Validators
} from "@angular/forms";
import {NgIf} from "@angular/common";
import {TablerIconsModule} from "angular-tabler-icons";
import {ToastrService} from "ngx-toastr";
import {UserDecisionOptionsComponent} from "../user-decision-options/user-decision-options.component";
import * as XLSX from 'xlsx';
import {AppPaginationExcelTableComponent} from "../excel-table/excel-table.component";
import {DivisionType} from "../../file";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {LocalizationPipe} from "../../../../../pipe/localization-pipe";
import {QaInputComponent} from "../qa-input/qa-input.component";


@Component({
  selector: 'file-upload-area',
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    TablerIconsModule,
    UserDecisionOptionsComponent,
    AppPaginationExcelTableComponent,
    MatProgressSpinnerModule,
    LocalizationPipe,
    QaInputComponent,
  ],
  templateUrl: './file-upload-area.component.html',
  styleUrls: ['./file-upload-area.component.scss'],
})
export class AppFileUploadAreaComponent implements OnInit, OnChanges {
  uploadedFiles: any[] = [];
  @Input() fileUploadOptionsList: any;
  @Input() formGroup: FormGroup;
  @Input() selectionType: string;
  @Input() acceptedTypes: string[];
  @Input() loadableFileTypes: string;
  @Input() maxFileSize: string;

  @Output() fileUploadEvent = new EventEmitter<File[]>();
  @Output() fileRemoveEvent = new EventEmitter<number>();
  @Output() userDecisionChangeEvent = new EventEmitter<[string, boolean, number]>();
  @Output() showUserDecisionSwitchChange = new EventEmitter<[string, boolean, number]>();
  @Output() updateSheetHeaderKeyChange = new EventEmitter<[number, number, string]>();
  @Output() columnMappingsChange = new EventEmitter<[string, string, string, string]>();
  @Output() updateColumnMappingsTypeChange = new EventEmitter<[string, string, string]>()
  @Output() divisionColumnsChange = new EventEmitter<string>()
  @Output() qaDataChangeEvent = new EventEmitter<any[]>();

  selectedDivisionType: string;
  selectedColumns = {}

  uploadedFilesWithUrl = [];
  sheetData: { name: string; columns: string[]; rows: unknown[] }[] = [];
  optionControls: Object = {
    "pdf": false,
    "doc": false,
    "txt": false,
    "image": false,
    "video": false,
    "excel": false,
    "organization": false,
  }
  divisionTypes: string[] = Object.keys(DivisionType);
  loading = false;

  constructor(private toastService: ToastrService, private _formBuilder: FormBuilder) {}

  ngOnInit() {
    // Form grubundaki dosyaları senkronize et
    this.syncUploadedFiles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formGroup'] && this.formGroup) {
      this.syncUploadedFiles();
    }
  }

  syncUploadedFiles() {
    if (this.formGroup) {
      const uploadedFilesControl = this.formGroup.get('uploadedFiles');
      if (uploadedFilesControl && uploadedFilesControl.value) {
        this.uploadedFiles = [...uploadedFilesControl.value];
        this.uploadedFilesWithUrl = this.uploadedFiles.map(file => ({
          url: URL.createObjectURL(file),
          file: file
        }));
      }
    }
  }

  changeUserDecision(updatedDecisionKey: string, userDecisionValue: boolean, sheetIndex: number) {
    this.userDecisionChangeEvent.emit([updatedDecisionKey, userDecisionValue, sheetIndex]);
  }

  openUserDecisionSwitch(fileType: string, value: boolean, sheetIndex: number) {
    this.showUserDecisionSwitchChange.emit([fileType, value, sheetIndex])
  }

  updateSheetHeaderKey(sheetIndex: number, columnIndex: number, value: string){
    this.updateSheetHeaderKeyChange.emit([sheetIndex, columnIndex, value]);
  }

  addColumnMappings(key: string, fieldName: string, type: string, dateType: string) {
    this.columnMappingsChange.emit([key, fieldName, type, dateType])
  }

  updateDivisionColumns(existingFieldName: string) {
    this.divisionColumnsChange.emit(existingFieldName)
  }

  selectDivisionType(type: DivisionType) {
    this.formGroup.get('divisionType').setValue(type);
    this.resetDivisionColumns();
  }

  minDivisionColumnValidation(min: number){
    return (control: AbstractControl): ValidationErrors | null => {
      if (control instanceof Array || control.hasOwnProperty('controls')) {
        const divisionColumns = control as any;
        return divisionColumns.length >= min ? null : {minDivisionColumnValidation: {requiredLength: min, actualLength: divisionColumns.length}}
      }
      return null
    }
  }

  resetDivisionColumns() {
    (this.formGroup.get('divisionColumns') as FormArray).clear();
    this.selectedColumns = {};
    (this.formGroup.get('divisionColumns') as FormArray).setValidators([Validators.required, this.minDivisionColumnValidation(2)]);
    (this.formGroup.get('divisionColumns') as FormArray).updateValueAndValidity();
  }

  getFileMimeTypeIcon(file: File) {
    switch (file.type) {
      case "application/pdf":
        return "file-type-pdf";
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.oasis.opendocument.text":
        return "file-type-docx";
      case "text/plain":
      case "text/markdown":
        return "file-type-txt";
      case "image/jpeg":
      case "image/png":
      case "image/gif":
      case "image/bmp":
      case "image/tiff":
      case "image/webp":
      case "image/vnd.microsoft.icon":
      case "image/svg+xml":
        return "photo-scan";
      case "video/mp4":
      case "video/x-msvideo":
      case "video/quicktime":
      case "video/x-ms-wmv":
      case "video/x-flv":
      case "video/x-matroska":
      case "video/webm":
      case "video/3gpp":
      case "video/mpeg":
      case "video/x-m4v":
        return "video";
      case "application/vnd.ms-excel":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.oasis.opendocument.spreadsheet":
      case "text/csv":
        return "file-spreadsheet";
      default:
        return "file-unknown";
    }
  }

  getOptionTypeIcon(key: string) {
    switch (key) {
      case "video":
        return "video"
      case "image":
        return "photo-scan"
      case "pdf":
        return "file-type-pdf"
      case "doc":
        return "file-type-docx"
      case "txt":
        return "file-type-txt";
      case "excel":
      case "organization":
        return "file-spreadsheet";
      default:
        return "file-unknown"
    }
  }

  onFileDrag($event: DragEvent) {
    $event.preventDefault();

    setTimeout(() => {
      if (this.uploadedFiles.length > 0) {
        const dropzone = document.getElementById("dropzone");
        dropzone.classList.add("file-upload-border")
        dropzone.classList.add("rounded")
      }
    }, 0);
  }

  onFileDrop($event: DragEvent, type: string) {
    $event.preventDefault();
    $event.stopPropagation();
    if ($event.dataTransfer?.files) {
      this.checkFileTypesAndAddToUploadedFiles($event.dataTransfer?.files, type);
      setTimeout(() => {
        if (this.uploadedFiles.length > 0) {
          const dropzone = document.getElementById("dropzone");
          dropzone?.classList.remove("file-upload-border", "rounded");
        }
      }, 0);
    }
  }

  uploadFiles(fileList: FileList, type: string): void {
    if (fileList.length > 0) {
      this.checkFileTypesAndAddToUploadedFiles(fileList, type)
    }
  }

  updateControlOptions(fileExtension: string, type: string, status: boolean){
    switch (fileExtension) {
      case "docx":
      case "doc":
      case "odt":
        this.optionControls["doc"] = status;
        break;
      case "pdf":
        this.optionControls["pdf"] = status;
        break;
      case "txt":
      case "md":
        this.optionControls["txt"] = status;
        break;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "tiff":
      case "tif":
      case "webp":
      case "ico":
      case "svg":
        this.optionControls["image"] = status;
        break;
      case "mp4":
      case "avi":
      case "mov":
      case "wmv":
      case "flv":
      case "mkv":
      case "webm":
      case "3gp":
      case "mpeg":
      case "mpg":
      case "m4v":
        this.optionControls["video"] = status;
        break;
      case "xlsx":
      case "xls":
      case "ods":
      case "csv":
        switch (type) {
          case "excel":
            this.optionControls["excel"] = status;
            break;
          case "organization":
            this.optionControls["organization"] = status;
            break;
        }
        break;
      default:
        this.toastService.error("Unsupported file type");
        break;
    }
  }

  checkFileTypesAndAddToUploadedFiles(fileList: FileList, type: string) {
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const fileName = file.name;
      const fileType = file.type
      const fileExtension = fileName.split(".").pop()?.toLowerCase();
      const isAllowedFileType = this.fileUploadOptionsList
        .filter((fileType) => fileType.type.toString() === type)
        .some((fileType) => fileType.allowedExtensions?.includes(fileExtension))
      if (!isAllowedFileType) {
        this.toastService.error(`Unsupported media type: ${fileName}`, "Error");
        continue
      }
      if (this.uploadedFiles.some((file: File) => file.name === fileName)) {
        this.toastService.error(`File is already exists: ${fileName}`, "Error");
        continue
      }
      this.uploadedFiles.push(file);
      this.uploadedFilesWithUrl.push({url: URL.createObjectURL(file), file: file});
      if (type !== 'scraper') {
        this.fileUploadEvent.emit(this.uploadedFiles)

        // Form grubunu güncelle
        if (this.formGroup) {
          const uploadedFilesControl = this.formGroup.get('uploadedFiles');
          if (uploadedFilesControl) {
            uploadedFilesControl.setValue(this.uploadedFiles);
            uploadedFilesControl.updateValueAndValidity();
            this.formGroup.updateValueAndValidity();
          }

          // Form validasyonunu manuel olarak tetikle
          setTimeout(() => {
            if (this.formGroup) {
              this.formGroup.updateValueAndValidity();
            }
          }, 0);
        }
      }
      this.updateControlOptions(fileExtension, type, true)
      const trueOptions: string[] = Object.keys(this.optionControls).filter((key) => this.optionControls[key])
      if (type === 'table' || type === 'organization') {
        this.readExcelSheets(file)
        trueOptions.forEach((key) => {
          this.openUserDecisionSwitch(key, true, 0)
        })
        return
      } else {
        trueOptions.forEach((key) => {
          this.openUserDecisionSwitch(key, true, null)
        })
      }
    }
  }

  cleanEmptyColumns(data: any[]): any[] {
    if (!data.length) return data;
    const keysToRemove = Object.keys(data[0]).filter(key =>
      data.every(row => row[key] === '')
    );
    return data.map(row => {
      keysToRemove.forEach(key => delete row[key]);
      return row;
    });
  }

  readExcelSheets(file: File){
    this.loading = true;
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      const arrayBuffer: ArrayBuffer = e.target.result;
      const workBook: XLSX.WorkBook = XLSX.read(arrayBuffer, {type: 'array', sheetRows: 5})

      // Önce sheets array'ini temizle
      if (this.formGroup.get('sheets')) {
        const sheetsArray = this.formGroup.get('sheets') as FormArray;
        sheetsArray.clear();
      }

      this.sheetData = workBook.SheetNames.map((sheetName: string, index: number) => {
        const sheet = workBook.Sheets[sheetName];
        const data = this.cleanEmptyColumns(XLSX.utils.sheet_to_json(sheet, {defval: ''}));
        if (data.length === 0) return {name: sheetName, rows: [], columns: []};
        const keys = Object.keys(data[0]);
        const range = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : { s: { c: 0 }, e: { c: 0 } };
        const columnCount = range.e.c - range.s.c + 1;

        if (this.formGroup.get('sheets')) {
          // Her sheet için yeni bir form group oluştur
          const newSheetFormGroup = this._formBuilder.group({
            sheetTitleExists: this._formBuilder.group({
              type: [['excel']],
              show: [false],
              value: [true],
            }),
            updateSheetTitles: this._formBuilder.group({
              type: [['excel']],
              show: [false],
              value: [true],
            }),
            sheetIndex: this._formBuilder.control(index, Validators.required),
            sheetHeaders: this._formBuilder.array([]),
          });

          // Her sütun için header group oluştur
          for (let i = 0; i < columnCount; i++) {
            const newSheetHeadersGroup = this._formBuilder.group({
              key: [keys[i] || ''],
              type: ['string']
            });
            (newSheetFormGroup.get('sheetHeaders') as FormArray).push(newSheetHeadersGroup);
          }

          (this.formGroup.get('sheets') as FormArray).push(newSheetFormGroup);
        }

        const firstRowObject = Object.values(Object.keys(data[0])).reduce((acc, val) => {
          acc[val] = String(val);
          return acc;
        }, {});
        data.unshift(firstRowObject);
        this.loading = false;
        return {
          name: sheetName,
          columns: keys,
          rows: data
        }
      })

      // Form validasyonunu güncelle
      if (this.formGroup) {
        this.formGroup.updateValueAndValidity();
      }
    }
    reader.readAsArrayBuffer(file)
  }

  removeUploadedFile(fileIndex: number, type: string): void {
    if (this.uploadedFiles.length > 0) {
      const fileExtension: string = this.uploadedFiles[fileIndex].name.split(".").pop()?.toLowerCase();
      this.uploadedFiles.splice(fileIndex, 1)
      this.uploadedFilesWithUrl.splice(fileIndex, 1);
      const falseOptions: string[] = Object.keys(this.optionControls).filter((key) => this.optionControls[key])
      if (type !== 'table') {
        falseOptions.forEach((key) => {
          this.openUserDecisionSwitch(key, false, null)
        })
      } else {
        falseOptions.forEach((key) => {
          this.openUserDecisionSwitch(key, false, 0)
        })
      }
      this.updateControlOptions(fileExtension, type, false)
      this.fileRemoveEvent.emit(fileIndex)

      // Dosya silindikten sonra güncel dosya listesini emit et
      this.fileUploadEvent.emit(this.uploadedFiles)

      // Form grubunu güncelle
      if (this.formGroup) {
        const uploadedFilesControl = this.formGroup.get('uploadedFiles');
        if (uploadedFilesControl) {
          uploadedFilesControl.setValue(this.uploadedFiles);
          uploadedFilesControl.updateValueAndValidity();
          this.formGroup.updateValueAndValidity();
        }

        // Excel dosyaları için sheets array'ini de temizle
        if (type === 'table' || type === 'organization') {
          const sheetsControl = this.formGroup.get('sheets');
          if (sheetsControl) {
            (sheetsControl as FormArray).clear();
            sheetsControl.updateValueAndValidity();
          }
        }
      }

      this.sheetData = [];

      // Form validasyonunu manuel olarak tetikle
      setTimeout(() => {
        if (this.formGroup) {
          this.formGroup.updateValueAndValidity();
        }
      }, 0);
    } else {
      this.toastService.error("Please select at least one file.", "Error")
    }
  }

  isExcelFile(){
    return this.acceptedTypes.includes(".xlsx") ||
      this.acceptedTypes.includes(".xls") ||
      this.acceptedTypes.includes(".ods") ||
      this.acceptedTypes.includes(".csv")
  }

  onQaDataChange(qaData: any[]) {
    this.qaDataChangeEvent.emit(qaData);
  }

  onQaFileUpload(file: File) {
  }

  protected readonly console = console;
}
